<template>
  <div class="category-form">
    <div class="form-group">
      <label>نام</label>
      <input type="text" v-model="form.name" class="form-input" :class="{ error: errors.name }" />
      <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
    </div>

    <div class="form-group">
      <label>والد</label>
      <div class="parent-selector" ref="parentTrigger" @click="toggleParentDropdown">
        <span>{{ parentLabel || 'بدون والد (ریشه)' }}</span>
        <svg class="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>
      <div v-if="parentDropdownOpen" class="parent-dropdown" :style="dropdownStyle">
        <div class="dropdown-search">
          <input
              type="text"
              v-model="parentSearch"
              placeholder="جستجو..."
              class="search-input"
              @click.stop
          />
        </div>
        <div class="dropdown-list">
          <div
              class="dropdown-item"
              :class="{ selected: form.parent === null }"
              @click="selectParent(null)"
          >
            — بدون والد (ریشه) —
          </div>
          <div
              v-for="opt in filteredParentOptions"
              :key="opt.id"
              class="dropdown-item"
              :class="{ selected: form.parent === opt.id }"
              @click="selectParent(opt.id)"
              :style="{ paddingRight: (opt.depth * 15 + 8) + 'px' }"
          >
            {{ opt.label }}
          </div>
        </div>
      </div>
      <span v-if="errors.parent" class="error-text">{{ errors.parent }}</span>
    </div>

    <div class="form-group">
      <label>تصویر (اختیاری)</label>
      <input type="file" accept="image/*" @change="onImageChange" class="file-input" />
      <div v-if="imagePreview" class="image-preview">
        <img :src="img_source(imagePreview)" />
        <button type="button" class="remove-image" @click="removeImage">✕</button>
      </div>
      <span v-if="errors.image" class="error-text">{{ errors.image }}</span>
    </div>

    <div class="form-actions">
      <button class="btn-secondary" @click="$emit('cancel')" :disabled="loading">انصراف</button>
      <button class="btn-primary" @click="submit" :disabled="loading">
        <span v-if="loading">در حال ارسال...</span>
        <span v-else>{{ mode === 'create' ? 'ایجاد' : 'ویرایش' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TreeForm',
  props: {
    mode: { type: String, required: true, validator: v => ['create', 'edit'].includes(v) },
    basePath: { type: String, default: 'feature' },
    categories: { type: Array, required: true },
    initialData: { type: Object, default: null }
  },
  data() {
    return {
      form: {
        name: '',
        parent: null,
        image: null,
      },
      imagePreview: null,
      parentDropdownOpen: false,
      parentSearch: '',
      dropdownStyle: {},
      errors: {},
      loading: false,
    };
  },
  computed: {
    flatCategoryOptions() {
      const out = [];
      const walk = (nodes, depth = 0) => {
        nodes.forEach(node => {
          out.push({
            id: node.id,
            label: node.name,
            depth: depth,
          });
          if (node.children && node.children.length) {
            walk(node.children, depth + 1);
          }
        });
      };
      walk(this.categories, 0);
      return out;
    },
    filteredParentOptions() {
      const q = this.parentSearch.trim().toLowerCase();
      if (!q) return this.flatCategoryOptions;
      return this.flatCategoryOptions.filter(opt =>
          opt.label.toLowerCase().includes(q)
      );
    },
    parentLabel() {
      if (this.form.parent === null) return '';
      const found = this.flatCategoryOptions.find(opt => opt.id === this.form.parent);
      return found ? found.label : '';
    }
  },
  mounted() {
    if (this.mode === 'edit' && this.initialData) {
      this.form.name = this.initialData.name || '';
      this.form.parent = this.initialData.parent.id || this.initialData.parent || null;
      if (this.initialData.image) {
        const url = typeof this.initialData.image === 'object' ? this.initialData.image.url : this.initialData.image;
        this.imagePreview = url;
        // توجه: فایل اصلی برای ویرایش نداریم، فقط پیش‌نمایش آدرس
      }
    }
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleParentDropdown() {
      if (this.parentDropdownOpen) {
        this.parentDropdownOpen = false;
        return;
      }
      const trigger = this.$refs.parentTrigger;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        this.dropdownStyle = {
          top: rect.bottom + 4 + 'px',
          left: rect.left + 'px',
          width: rect.width + 'px'
        };
      }
      this.parentDropdownOpen = true;
    },
    selectParent(id) {
      this.form.parent = id;
      this.parentDropdownOpen = false;
    },
    handleClickOutside(e) {
      if (this.parentDropdownOpen && !this.$el.contains(e.target)) {
        this.parentDropdownOpen = false;
      }
    },
    onImageChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.form.image = file;
        const reader = new FileReader();
        reader.onload = ev => this.imagePreview = ev.target.result;
        reader.readAsDataURL(file);
      } else {
        this.form.image = null;
        this.imagePreview = null;
      }
    },
    removeImage() {
      this.form.image = null;
      this.imagePreview = null;
      // ریست فایل اینپوت
      const input = this.$el.querySelector('input[type="file"]');
      if (input) input.value = '';
    },
    submit() {
      this.loading = true;
      this.errors = {};

      const fd = new FormData();
      fd.append('name', this.form.name);
      if (this.form.parent !== null) {
        fd.append('parent', this.form.parent);
      }
      if (this.form.image) {
        fd.append('image', this.form.image);
      }

      const method = this.mode === 'create' ? 'post' : 'put';
      const url = this.mode === 'create' ?
          this.endpoint(`product/${this.basePath}`) : this.endpoint(`product/${this.basePath}/${this.initialData.id}`);

      this.request({
        url,
        method,
        data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },
        success: (data) => {
          this.loading = false;
          this.$emit('submitted', data);
        },
        error: (err) => {
          this.loading = false;
          const response = err.response.data || err;
          if (typeof response === 'object') {
            const formatted = {};
            for (const key in response) {
              formatted[key] = Array.isArray(response[key])
                  ? response[key].join(' ، ')
                  : response[key];
            }
            this.errors = formatted;
          } else {
            this.errors = { _error: 'خطا در ارتباط با سرور' };
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.category-form {
  color: #e0e0e0;
}

.form-group {
  margin-bottom: 1.5rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #ccc;
}

.form-input {
  width: 100%;
  padding: 0.6rem 1rem;
  background: #333;
  border: 1px solid #555;
  border-radius: 6px;
  color: #fff;
  outline: none;
}
.form-input.error {
  border-color: #f56565;
}

.parent-selector {
  background: #333;
  border: 1px solid #555;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}
.parent-selector span {
  color: #aaa;
}
.dropdown-arrow {
  width: 16px;
  height: 16px;
  color: #888;
}

.parent-dropdown {
  position: fixed;
  z-index: 1100;
  background: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  max-height: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.dropdown-search {
  padding: 0.5rem;
  border-bottom: 1px solid #444;
}
.search-input {
  width: 100%;
  padding: 0.4rem 0.8rem;
  background: #3a3a3a;
  border: 1px solid #555;
  border-radius: 4px;
  color: #fff;
}
.dropdown-list {
  overflow-y: auto;
  max-height: 200px;
}
.dropdown-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: #ddd;
  border-bottom: 1px solid #3a3a3a;
}
.dropdown-item:hover {
  background: #3a4a5a;
}
.dropdown-item.selected {
  background: #2b6e4f;
  color: white;
}

.file-input {
  background: #333;
  color: #ccc;
  padding: 0.4rem;
  border-radius: 4px;
  width: 100%;
}

.image-preview {
  margin-top: 0.8rem;
  position: relative;
  display: inline-block;
}
.image-preview img {
  max-width: 150px;
  max-height: 150px;
  border-radius: 8px;
  border: 2px solid #555;
}
.remove-image {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #b91c1c;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-text {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.8rem;
  color: #f56565;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}
.btn-primary {
  background: #2b6e4f;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
}
.btn-secondary {
  background: #4a5568;
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  cursor: pointer;
}
.btn-primary:disabled, .btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>